import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const useKeyboardShortcuts = (shortcuts = {}) => {
  const navigate = useNavigate();
  const shortcutsRef = useRef(shortcuts);

  // Update shortcuts ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Don't trigger shortcuts when user is typing in input fields
      const activeElement = document.activeElement;
      const isInputActive = activeElement?.tagName === 'INPUT' || 
                           activeElement?.tagName === 'TEXTAREA' || 
                           activeElement?.contentEditable === 'true';

      if (isInputActive && !event.ctrlKey && !event.altKey) {
        return;
      }

      // Create key combination string
      const keys = [];
      if (event.ctrlKey) keys.push('ctrl');
      if (event.altKey) keys.push('alt');
      if (event.shiftKey) keys.push('shift');
      keys.push(event.key.toLowerCase());
      
      const keyCombo = keys.join('+');

      // Global shortcuts
      const globalShortcuts = {
        // Navigation shortcuts
        'ctrl+1': () => navigate('/dashboard-overview'),
        'ctrl+2': () => navigate('/employee-management'),
        'ctrl+3': () => navigate('/applicant-tracking-system'),
        'ctrl+4': () => navigate('/payroll-management'),
        'ctrl+5': () => navigate('/invoice-billing-management'),
        'ctrl+6': () => navigate('/compliance-statutory-reports'),
        
        // Common actions
        'ctrl+n': () => {
          // Trigger new/add action based on current page
          const path = window.location.pathname;
          if (path.includes('employee-management')) {
            document.querySelector('[data-shortcut="add-employee"]')?.click();
          } else if (path.includes('invoice-billing')) {
            document.querySelector('[data-shortcut="create-invoice"]')?.click();
          } else if (path.includes('applicant-tracking')) {
            document.querySelector('[data-shortcut="add-application"]')?.click();
          }
        },
        
        'ctrl+s': (e) => {
          e.preventDefault();
          // Trigger save action if form is open
          document.querySelector('[data-shortcut="save"]')?.click();
        },
        
        'escape': () => {
          // Close modals or cancel actions
          document.querySelector('[data-shortcut="cancel"]')?.click();
          document.querySelector('[data-shortcut="close-modal"]')?.click();
        },
        
        // Search shortcut
        'ctrl+k': (e) => {
          e.preventDefault();
          document.querySelector('[data-shortcut="search"]')?.focus();
        },
        
        // Refresh data
        'f5': (e) => {
          e.preventDefault();
          document.querySelector('[data-shortcut="refresh"]')?.click();
        }
      };

      // Check global shortcuts first
      if (globalShortcuts[keyCombo]) {
        event.preventDefault();
        globalShortcuts[keyCombo](event);
        return;
      }

      // Check custom shortcuts passed to hook
      if (shortcutsRef.current[keyCombo]) {
        event.preventDefault();
        shortcutsRef.current[keyCombo](event);
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate]);

  return {
    // Helper function to get shortcut hint text
    getShortcutHint: (action) => {
      const shortcutMap = {
        'dashboard': 'Ctrl+1',
        'employees': 'Ctrl+2',
        'applications': 'Ctrl+3',
        'payroll': 'Ctrl+4',
        'invoices': 'Ctrl+5',
        'compliance': 'Ctrl+6',
        'new': 'Ctrl+N',
        'save': 'Ctrl+S',
        'search': 'Ctrl+K',
        'refresh': 'F5',
        'cancel': 'Esc'
      };
      return shortcutMap[action] || '';
    }
  };
};

export default useKeyboardShortcuts;